import React, { useState, useRef, useCallback, useEffect } from 'react';
import axios from 'axios';
import classNames from 'classnames';
import { FormattedMessage } from '../../util/reactIntl';
import { IconSpinner } from '..';

import css from './LiveCameraCapture.module.css';

/**
 * LiveCameraCapture - Component for capturing photos and videos directly from device camera
 * 
 * Features:
 * - Photo capture (single shot)
 * - Video recording (with duration limit)
 * - Preview before upload
 * - Permission handling
 * - Fallback to gallery if camera unavailable
 * 
 * @param {string} mode - 'photo' or 'video'
 * @param {number} videoDurationLimit - Maximum video duration in seconds (default 30)
 * @param {function} onCapture - Callback with captured file info { url, name }
 * @param {function} onCancel - Callback when user cancels
 */
const LiveCameraCapture = props => {
  const {
    mode = 'photo',
    videoDurationLimit = 30,
    onCapture,
    onCancel,
    className,
  } = props;

  const isDev = typeof window !== 'undefined' && window.location?.hostname === 'localhost';
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  
  const [stream, setStream] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [capturedVideoBlob, setCapturedVideoBlob] = useState(null);
  const [capturedVideoUrl, setCapturedVideoUrl] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);
  const [permissionDenied, setPermissionDenied] = useState(false);

  // Check if getUserMedia is supported
  const isSupported = !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);

  // Initialize camera
  useEffect(() => {
    if (!isSupported) {
      setError('camera-not-supported');
      return;
    }

    const startCamera = async () => {
      try {
        const constraints = {
          video: {
            facingMode: 'environment', // Use back camera on mobile
            width: { ideal: 1920 },
            height: { ideal: 1080 }
          },
          audio: mode === 'video'
        };

        const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
        mediaStreamRef.current = mediaStream;
        setStream(mediaStream);
        
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
        }
      } catch (err) {
        console.error('Camera access error:', err);
        if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
          setPermissionDenied(true);
          setError('camera-permission-denied');
        } else if (err.name === 'NotFoundError') {
          setError('camera-not-found');
        } else {
          setError('camera-access-failed');
        }
      }
    };

    startCamera();

    // Cleanup on unmount
    return () => {
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, [isSupported, mode]);

  // Recording timer
  useEffect(() => {
    let interval;
    if (isRecording) {
      interval = setInterval(() => {
        setRecordingTime(prev => {
          const newTime = prev + 1;
          if (newTime >= videoDurationLimit) {
            stopRecording();
          }
          return newTime;
        });
      }, 1000);
    } else {
      setRecordingTime(0);
    }
    return () => clearInterval(interval);
  }, [isRecording, videoDurationLimit]);

  // Capture photo
  const capturePhoto = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    // Set canvas dimensions to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    // Draw current video frame to canvas
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    
    // Convert canvas to blob
    canvas.toBlob(blob => {
      if (blob) {
        const url = URL.createObjectURL(blob);
        setCapturedPhoto({ blob, url });
      }
    }, 'image/jpeg', 0.9);
  }, []);

  // Start video recording
  const startRecording = useCallback(() => {
    if (!mediaStreamRef.current) return;

    recordedChunksRef.current = [];
    
    try {
      const options = { mimeType: 'video/webm;codecs=vp9' };
      // Fallback for browsers that don't support vp9
      if (!MediaRecorder.isTypeSupported(options.mimeType)) {
        options.mimeType = 'video/webm';
      }

      const mediaRecorder = new MediaRecorder(mediaStreamRef.current, options);
      
      mediaRecorder.ondataavailable = event => {
        if (event.data && event.data.size > 0) {
          recordedChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = () => {
        const blob = new Blob(recordedChunksRef.current, { type: 'video/webm' });
        const url = URL.createObjectURL(blob);
        setCapturedVideoBlob(blob);
        setCapturedVideoUrl(url);
      };
      
      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start(100); // Collect data every 100ms
      setIsRecording(true);
    } catch (err) {
      console.error('Recording error:', err);
      setError('recording-failed');
    }
  }, []);

  // Stop video recording
  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  }, [isRecording]);

  // Upload captured file to Azure
  const uploadFile = useCallback(async () => {
    const file = mode === 'photo' ? capturedPhoto?.blob : capturedVideoBlob;
    if (!file) return;

    setIsUploading(true);
    const formData = new FormData();
    const filename = `${mode}-${Date.now()}.${mode === 'photo' ? 'jpg' : 'webm'}`;
    formData.append('image', file, filename);

    try {
      const resp = await axios.post(
        `${isDev ? 'http://localhost:3500' : ''}/api/azure-upload`,
        formData
      );
      const uploadedFile = resp?.data?.file;
      
      if (onCapture) {
        onCapture(uploadedFile);
      }
      
      // Stop camera after successful upload
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach(track => track.stop());
      }
    } catch (error) {
      console.error('Upload error:', error);
      setError('upload-failed');
    } finally {
      setIsUploading(false);
    }
  }, [mode, capturedPhoto, capturedVideoBlob, isDev, onCapture]);

  // Retake (clear captured media and restart camera)
  const retake = useCallback(() => {
    setCapturedPhoto(null);
    setCapturedVideoBlob(null);
    if (capturedVideoUrl) {
      URL.revokeObjectURL(capturedVideoUrl);
      setCapturedVideoUrl(null);
    }
    setError(null);
  }, [capturedVideoUrl]);

  // Cancel and close
  const handleCancel = useCallback(() => {
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
    }
    if (capturedVideoUrl) {
      URL.revokeObjectURL(capturedVideoUrl);
    }
    if (onCancel) {
      onCancel();
    }
  }, [capturedVideoUrl, onCancel]);

  const classes = classNames(css.root, className);

  // Error states
  if (error || permissionDenied) {
    return (
      <div className={classes}>
        <div className={css.errorContainer}>
          <div className={css.errorIcon}>⚠️</div>
          <h3 className={css.errorTitle}>
            <FormattedMessage id={`LiveCameraCapture.${error || 'error'}`} />
          </h3>
          <p className={css.errorMessage}>
            <FormattedMessage id={`LiveCameraCapture.${error}Message`} />
          </p>
          {permissionDenied && (
            <p className={css.errorHint}>
              <FormattedMessage id="LiveCameraCapture.permissionHint" />
            </p>
          )}
          <button type="button" onClick={handleCancel} className={css.cancelButton}>
            <FormattedMessage id="LiveCameraCapture.close" />
          </button>
        </div>
      </div>
    );
  }

  // Photo preview
  if (mode === 'photo' && capturedPhoto) {
    return (
      <div className={classes}>
        <div className={css.previewContainer}>
          <img src={capturedPhoto.url} alt="Captured" className={css.preview} />
          <div className={css.previewActions}>
            {isUploading ? (
              <div className={css.uploadingIndicator}>
                <IconSpinner />
                <FormattedMessage id="LiveCameraCapture.uploading" />
              </div>
            ) : (
              <>
                <button type="button" onClick={retake} className={css.secondaryButton}>
                  <FormattedMessage id="LiveCameraCapture.retake" />
                </button>
                <button type="button" onClick={uploadFile} className={css.primaryButton}>
                  <FormattedMessage id="LiveCameraCapture.usePhoto" />
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Video preview
  if (mode === 'video' && capturedVideoBlob) {
    return (
      <div className={classes}>
        <div className={css.previewContainer}>
          <video src={capturedVideoUrl} controls className={css.preview} />
          <div className={css.previewActions}>
            {isUploading ? (
              <div className={css.uploadingIndicator}>
                <IconSpinner />
                <FormattedMessage id="LiveCameraCapture.uploading" />
              </div>
            ) : (
              <>
                <button type="button" onClick={retake} className={css.secondaryButton}>
                  <FormattedMessage id="LiveCameraCapture.retake" />
                </button>
                <button type="button" onClick={uploadFile} className={css.primaryButton}>
                  <FormattedMessage id="LiveCameraCapture.useVideo" />
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Camera view
  return (
    <div className={classes}>
      <div className={css.cameraContainer}>
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className={css.video}
        />
        <canvas ref={canvasRef} className={css.hiddenCanvas} />
        
        {isRecording && (
          <div className={css.recordingIndicator}>
            <div className={css.recordingDot} />
            <span className={css.recordingTime}>
              {recordingTime}s / {videoDurationLimit}s
            </span>
          </div>
        )}

        <div className={css.controls}>
          <button type="button" onClick={handleCancel} className={css.cancelButton}>
            <FormattedMessage id="LiveCameraCapture.cancel" />
          </button>
          
          {mode === 'photo' && (
            <button type="button" onClick={capturePhoto} className={css.captureButton} disabled={!stream}>
              <div className={css.captureIcon} />
            </button>
          )}
          
          {mode === 'video' && !isRecording && (
            <button type="button" onClick={startRecording} className={css.captureButton} disabled={!stream}>
              <div className={css.recordIcon} />
            </button>
          )}
          
          {mode === 'video' && isRecording && (
            <button type="button" onClick={stopRecording} className={css.stopButton}>
              <div className={css.stopIcon} />
            </button>
          )}
          
          <div className={css.placeholder} />
        </div>
      </div>
    </div>
  );
};

export default LiveCameraCapture;
